import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Map } from 'immutable';
import classNames from 'classnames';
import { Portal } from 'react-portal';

import {
  FAVORITE_SEARCH_SLOTS,
  fipsMap,
  countyMap,
  clearSuggestion,
  loadSearch,
  setSuggestionValue,
  setSuggestionLabel,
  searchSuggestions,
  setRecentSuggestions,
  selectSuggestion,
  setSuggestions,
  clearFilter,
  clearAll,
  clearLocalFilter,
  selectFavoriteSearches,
  selectFavoriteSearchCounts,
  selectFavoriteSearchLoading,
  selectFavoriteSearchStale,
  clearLeadListApiData,
  setSelectedSaveSearch,
  setFavoriteItem,
  selectFavoriteSelectedName,
  selectFilter,
  selectLocalFilter,
  selectLocation,
  refreshSearch,
} from 'data/search';

import { toAppliedFilterContext } from './appliedFilterContext';

import Button from 'components/base/Button';
import SVG from 'components/base/SVG';
import HeaderSearchItem from 'components/HeaderSearchItem';
import SuggestedInput from 'components/SuggestedInput';

import RestrictedContent from 'app/components/RestrictedContent';
import { Permissions } from 'data/user/constants';
import VoiceConversationController, {
  VOICE_NETWORK_ERROR_TITLE,
  VOICE_UNSUPPORTED_BROWSER_TOOLTIP,
  VOICE_WAVEFORM_MAX_HEIGHT,
  VOICE_WAVEFORM_MIN_HEIGHT,
  clampVoiceAmplitude,
  getInitialVoiceWaveformSamples,
} from 'utils/Voice/VoiceConversationController';
import MicPermissionModal from 'components/Voice/MicPermissionModal';

import AllSearches from './AllSearches';
import SearchModeToggle, { SearchModeOptions } from './SearchModeToggle';
import SearchChatPanel, {
  hasPersistentSearchChatContent,
  resetPersistentSearchSession,
} from './SearchChatPanel';
import css from './style.scss';
import clearSearch from '../../../assets/images/clearSearch.svg';
import aiVoiceIcon from '../../../assets/images/aivoicesearch.svg';
import endButtonIcon from '../../../assets/images/Endbuttonicon.svg';
import SearchFilterNew from './searchFilterNew/SearchFilterNew';
import { TagFilterProvider } from './searchFilterNew/TagFilterContext';
import NewUIFilterSaved from './searchFilterNew/UIFilterSave';
import HeaderMlsFilter from './searchFilterNew/panel/right/HeaderMlsFilter';

const RECENT_SUGGESTION_PATH = 'PropStream.RecentSuggestions.1';
const MAX_RECENT_SUGGESTIONS = 15;

const FavoriteSlots = Array.from({ length: FAVORITE_SEARCH_SLOTS }, (v, i) => i + 1);

const VOICE_UNSUPPORTED_BROWSER_TOOLTIP_ID = 'voice-unsupported-browser-tooltip';
// Must match .voiceUnsupportedTooltip's width/gap in style.scss. The bubble is
// portalled to <body> (so it can paint above .chatPanel), which means its
// viewport coordinates are computed here rather than by CSS.
const VOICE_UNSUPPORTED_TOOLTIP_WIDTH = 312;
const VOICE_UNSUPPORTED_TOOLTIP_GAP = 10;
const LOCATION_SEARCH_PLACEHOLDER = 'Enter State, County, City, Zip Code(s) or APN #';

const renderMicControlIcon = (muted) => (
  <svg className={css.voiceControlIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M8 10.4c1.5 0 2.7-1.2 2.7-2.7V4c0-1.5-1.2-2.7-2.7-2.7S5.3 2.5 5.3 4v3.7c0 1.5 1.2 2.7 2.7 2.7z"
      fill="currentColor"
    />
    <path
      d="M12.7 7.2c.4 0 .7.3.7.7 0 2.5-1.8 4.5-4.1 4.9v1.2h1.6c.4 0 .7.3.7.7s-.3.7-.7.7H5.1c-.4 0-.7-.3-.7-.7s.3-.7.7-.7h1.6v-1.2c-2.3-.4-4.1-2.4-4.1-4.9 0-.4.3-.7.7-.7s.7.3.7.7c0 2.2 1.8 3.6 4 3.6s4-1.4 4-3.6c0-.4.3-.7.7-.7z"
      fill="currentColor"
    />
    {muted && (
      <path d="M2.3 13.7 13.7 2.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    )}
  </svg>
);

const renderSoundControlIcon = (muted) => (
  <svg className={css.voiceControlIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M2.2 6.1v3.8h2.5l3.4 2.8c.4.3 1 .1 1-.4V3.7c0-.5-.6-.7-1-.4L4.7 6.1H2.2z"
      fill="currentColor"
    />
    {!muted && (
      <>
        <path
          d="M10.8 5.4c.8.7 1.2 1.6 1.2 2.6s-.4 1.9-1.2 2.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <path
          d="M12.4 3.8c1.2 1.1 1.9 2.6 1.9 4.2s-.7 3.1-1.9 4.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
      </>
    )}
    {muted && (
      <path
        d="m11.1 6.1 3 3m0-3-3 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    )}
  </svg>
);

// ATAI-194: reuses the shared SVGsprite icon system (components/base/SVG),
// not a one-off inline glyph — open_in_full/close_fullscreen symbols added
// to SVGsprite for this ticket, colored via currentColor + CSS state
// (aiExpandToggle/aiExpandActive/:active) instead of swapping pre-colored
// image assets per hover/clicked state.
const renderExpandCollapseIcon = (chatOpen) =>
  chatOpen ? (
    <SVG icon="iconCollapseFullscreen" className={css.aiCollapseIcon} aria-hidden="true" />
  ) : (
    <SVG icon="iconExpandFullscreen" className={css.aiExpandIcon} aria-hidden="true" />
  );

// ATAI-201: swapped the hand-drawn waveform+sparkle glyph for the exact
// asset exported from Figma — symbol mark didn't match design documentation.
const renderEndCallIcon = () => (
  <img className={css.voiceEndIcon} src={endButtonIcon} alt="" aria-hidden="true" />
);

const renderVoiceWaveform = (samples, isReactive = false) => {
  const waveformSamples = samples || getInitialVoiceWaveformSamples();

  return (
    <span
      className={classNames(css.voiceWaveform, css.voiceInputWaveform, {
        [css.voiceWaveformReactive]: isReactive,
      })}
      aria-hidden="true"
    >
      {waveformSamples.map((height, idx) => {
        const safeHeight = Math.max(
          VOICE_WAVEFORM_MIN_HEIGHT,
          Math.min(VOICE_WAVEFORM_MAX_HEIGHT, height || VOICE_WAVEFORM_MIN_HEIGHT)
        );
        const level = clampVoiceAmplitude(
          (safeHeight - VOICE_WAVEFORM_MIN_HEIGHT) /
            (VOICE_WAVEFORM_MAX_HEIGHT - VOICE_WAVEFORM_MIN_HEIGHT)
        );
        const style = {
          '--voice-waveform-height': `${safeHeight}px`,
          '--voice-waveform-opacity': `${(0.5 + level * 0.5).toFixed(2)}`,
          '--voice-waveform-scale': `${(0.8 + level * 0.4).toFixed(2)}`,
        };

        if (!isReactive) style.animationDelay = `${(idx % 10) * 0.05}s`;

        return <span key={idx} style={style} />;
      })}
    </span>
  );
};

const getRecentSuggestions = () => {
  try {
    return JSON.parse(window.localStorage.getItem(RECENT_SUGGESTION_PATH)).filter(
      (i) => Boolean(i) && Boolean(i.label)
    );
  } catch (e) {
    return [];
  }
};

const setRecentSuggestion = (item) => {
  let items = getRecentSuggestions();

  const key = (item.label || '').trim().toLowerCase();

  if (key !== '') {
    items = items.filter((s) => s.key !== key);
    items.unshift({ ...item, key });
    items.length = Math.min(items.length, MAX_RECENT_SUGGESTIONS);

    window.localStorage.setItem(RECENT_SUGGESTION_PATH, JSON.stringify(items));
  }

  return items;
};

// TODO: This should not exist, and APN suggestions should come from the server. County ID should be used instead of FIPS.
const parseSuggestion = (query) => {
  let localResults = [];

  /**
   * Check for county matches. Allow:
   * Orange County, CA (County Only)
   * #93974065, Orange County, CA (APN + County)
   * 06059; (FIPS Only)
   * 06059;93974065
   *
   * Use local results only if this is a FIPS and/or APN search
   */
  if (query !== '') {
    // Check for FIPS pattern
    let countyQuery = query;
    let match = /^(\d{5})#([a-z0-9\s-\s.]+)?$/i.exec(query);

    if (match && match[1]) {
      // Convert to "<apn>, <county>, <state> format
      const apn = match[2] && match[2].trim();
      const county = fipsMap[match[1]];
      if (county) countyQuery = `${apn ? `#${apn}, ` : ''}${county.search}, ${county.state}`;
    }

    match =
      /^((((apn)?#)([a-z0-9\s-\s.]+)),)?([a-z\s'-]+)(,\s*([a-z]{2}))?$/i.exec(countyQuery) || [];
    const stateMatch = match[8];
    const countyMatch = match[6] || '';
    const apn = match[5] ? match[5].trim() : null;

    if (stateMatch && countyMatch.length > 3 && apn) {
      // const county = countyMap[`${match[3].trim().replace(/\s+county$/i, '')}-${match[5].trim()}`.toLowerCase()];
      const search = countyMatch
        .toLowerCase()
        .replace(/[^a-z]+/g, ' ')
        .replace(/\s+county$/, '')
        .trim();
      const explicitCountySearch = countyMatch.toLowerCase().includes(' county');

      // Pull all matching counties for respective state. Ignore independent cities / non-counties unless "county" is in the search string OR APN is provided.
      // If neither of those is true then the independent city should already be in the Bing results.
      localResults = localResults.concat(
        (countyMap[stateMatch.toUpperCase()] || [])
          .filter((c) => c.search.includes(search) && (c.county || explicitCountySearch || apn))
          .map((county) => {
            const label = `${apn ? `APN# ${apn}, ` : ''}${county.name}`;
            // return { name, address: { fips: county.fips, apn, formattedAddress: name }, score: (apn ? 5 : 0) + (explicitCountySearch ? 3 : 1) };
            return { label, fips: county.fips, apn };
          })
      );
    }
  }

  return localResults;
};

class Header extends PureComponent {
  constructor(props) {
    super(props);
    this.searchContainerRef = React.createRef();
    this.voiceTooltipHostRef = React.createRef();
    this.searchModeDividerRef = React.createRef();
    this.state = {
      searchItem: '',
      searchMode: SearchModeOptions.LOCATION,
      aiInputText: '',
      aiInputClearToken: 0,
      chatOpen: false,
      aiPendingPrompt: null,
      aiSubmitError: null,
      voiceError: null,
      chatLoading: false,
      aiChatHasContent: hasPersistentSearchChatContent(),
      chatResetToken: 0,
      voiceRecognitionSupported: false,
      voiceOutputSupported: false,
      voiceListening: false,
      voiceModeActive: false,
      voiceSpeaking: false,
      voiceTranscript: '',
      micMuted: false,
      voiceOutputMuted: false,
      voiceReplyPlaying: false,
      voiceAudioReactive: false,
      voiceWaveformSamples: null,
      shouldSpeakNextAiResponse: false,
      micPermissionBlocked: false,
      voiceNetworkError: false,
      voiceUnsupportedTooltipVisible: false,
      voiceUnsupportedTooltipPosition: null,
    };
    this.handleClear = this.handleClear.bind(this);
    this.handleClearAiInput = this.handleClearAiInput.bind(this);
    this.handleClearSuggestion = this.handleClearSuggestion.bind(this);
    this.handleChangeSuggestion = this.handleChangeSuggestion.bind(this);
    this.handleFetchSuggestion = this.handleFetchSuggestion.bind(this);
    this.handleFavoriteClick = this.handleFavoriteClick.bind(this);
    this.handleSearchModeChange = this.handleSearchModeChange.bind(this);
    this.handleAiSearchSubmit = this.handleAiSearchSubmit.bind(this);
    this.handleExpandToggle = this.handleExpandToggle.bind(this);
    this.handleChatContentChange = this.handleChatContentChange.bind(this);
    this.handleChatLoadingChange = this.handleChatLoadingChange.bind(this);
    this.handleVoiceToggle = this.handleVoiceToggle.bind(this);
    this.handleVoiceEnd = this.handleVoiceEnd.bind(this);
    this.handleMicMuteToggle = this.handleMicMuteToggle.bind(this);
    this.handleVoiceOutputMuteToggle = this.handleVoiceOutputMuteToggle.bind(this);
    this.handleAssistantResponseComplete = this.handleAssistantResponseComplete.bind(this);
    this.handleMicPermissionRetry = this.handleMicPermissionRetry.bind(this);
    this.handleMicPermissionDismiss = this.handleMicPermissionDismiss.bind(this);
    this.handleVoiceNetworkRetry = this.handleVoiceNetworkRetry.bind(this);
    this.handleVoiceNetworkDismiss = this.handleVoiceNetworkDismiss.bind(this);
    this.handleVoiceUnsupportedTooltipOpen = this.handleVoiceUnsupportedTooltipOpen.bind(this);
    this.handleVoiceUnsupportedTooltipClose = this.handleVoiceUnsupportedTooltipClose.bind(this);
    this.handleVoiceUnsupportedTooltipKeyDown =
      this.handleVoiceUnsupportedTooltipKeyDown.bind(this);
    this.handleVoiceUnsupportedTooltipDismiss =
      this.handleVoiceUnsupportedTooltipDismiss.bind(this);
    this.repositionVoiceUnsupportedTooltip = this.repositionVoiceUnsupportedTooltip.bind(this);
    this.focusAiSearchInputIfActive = this.focusAiSearchInputIfActive.bind(this);
    this.getPlaceholder = this.getPlaceholder.bind(this);

    this.searchTimer = null;
    this.voiceUnsupportedTooltipHideTimer = null;
    this.voiceUnsupportedTooltipFocused = false;
    this.isUnmounted = false;

    // ATAI-323: the voice engine (recognition, speech, waveform, timers, mutes)
    // lives in the shared controller; its state keys are mirrored into this
    // component's state under the same names, so render reads them unchanged.
    this.voice = new VoiceConversationController({
      // A voice event always cleared a stale AI Search submit error; keep that.
      onStateChange: (partial) =>
        this.safeSetState('voiceError' in partial ? { ...partial, aiSubmitError: null } : partial),
      onSubmit: (text) => this.handleAiSearchSubmit(text, { voice: true }),
      onTranscript: (text) => this.safeSetState({ aiInputText: text }),
    });
  }

  componentDidMount() {
    this.isUnmounted = false;
    this.props.setRecentSuggestions(getRecentSuggestions(), true);
    this.voice.detectSupport();
    this.attachInputListeners();
  }

  componentDidUpdate(prevProps, prevState) {
    // SuggestedInput remounts (key={`${searchMode}-${aiInputClearToken}`}) on
    // a mode switch OR an explicit clear (handleClear/handleClearAiInput
    // both bump aiInputClearToken), which replaces the
    // underlying <input> node — re-bind the listeners below to the fresh
    // node so Enter/focus/blur keep working after either kind of remount.
    if (
      prevState.searchMode !== this.state.searchMode ||
      prevState.aiInputClearToken !== this.state.aiInputClearToken
    ) {
      this.attachInputListeners();
    }
  }

  safeSetState(nextState, callback) {
    if (this.isUnmounted) return;
    this.setState(nextState, callback);
  }

  attachInputListeners() {
    const inputEl = this.searchContainerRef.current?.querySelector('input');
    if (inputEl) {
      inputEl.addEventListener('focus', () => (inputEl.placeholder = ''));
      inputEl.addEventListener('blur', () => (inputEl.placeholder = this.getPlaceholder()));
      inputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && this.state.searchMode === SearchModeOptions.AI) {
          e.preventDefault();
          e.stopPropagation();
          this.handleAiSearchSubmit();
        }
      });
    }
  }

  getPlaceholder() {
    return this.state.searchMode === SearchModeOptions.AI
      ? "Describe what you're looking for"
      : LOCATION_SEARCH_PLACEHOLDER;
  }

  handleMicPermissionRetry() {
    this.voice.retryMicPermission();
  }

  handleMicPermissionDismiss() {
    this.voice.dismissMicPermission();
  }

  handleVoiceNetworkRetry() {
    this.voice.retryNetworkError();
  }

  handleVoiceNetworkDismiss() {
    this.voice.dismissNetworkError();
  }

  clearVoiceUnsupportedTooltipHideTimer() {
    if (this.voiceUnsupportedTooltipHideTimer) {
      clearTimeout(this.voiceUnsupportedTooltipHideTimer);
      this.voiceUnsupportedTooltipHideTimer = null;
    }
  }

  // The bubble is centred on the voice button and sits below the search bar, but
  // it renders in a body-level Portal, so those are viewport coordinates.
  getVoiceUnsupportedTooltipPosition() {
    if (typeof window === 'undefined') return null;

    const container = this.searchContainerRef.current?.getBoundingClientRect();
    const host = this.voiceTooltipHostRef.current?.getBoundingClientRect();
    if (!container || !host) return null;

    return {
      top: container.bottom + VOICE_UNSUPPORTED_TOOLTIP_GAP,
      left: host.left + host.width / 2 - VOICE_UNSUPPORTED_TOOLTIP_WIDTH / 2,
    };
  }

  // Fixed coordinates go stale the moment the page scrolls or the window resizes,
  // so they are recomputed for as long as the bubble is open.
  repositionVoiceUnsupportedTooltip() {
    if (!this.state.voiceUnsupportedTooltipVisible) return;
    const voiceUnsupportedTooltipPosition = this.getVoiceUnsupportedTooltipPosition();
    if (voiceUnsupportedTooltipPosition) this.safeSetState({ voiceUnsupportedTooltipPosition });
  }

  bindVoiceUnsupportedTooltipReflow(bind) {
    if (typeof window === 'undefined') return;
    const fn = bind ? window.addEventListener : window.removeEventListener;
    fn.call(window, 'scroll', this.repositionVoiceUnsupportedTooltip, true);
    fn.call(window, 'resize', this.repositionVoiceUnsupportedTooltip);
  }

  handleVoiceUnsupportedTooltipOpen(e) {
    if (this.state.voiceRecognitionSupported) return;

    const voiceUnsupportedTooltipPosition = this.getVoiceUnsupportedTooltipPosition();
    if (!voiceUnsupportedTooltipPosition) return;

    if (e && e.type === 'focus') this.voiceUnsupportedTooltipFocused = true;
    this.clearVoiceUnsupportedTooltipHideTimer();
    if (!this.state.voiceUnsupportedTooltipVisible) this.bindVoiceUnsupportedTooltipReflow(true);
    this.safeSetState({
      voiceUnsupportedTooltipVisible: true,
      voiceUnsupportedTooltipPosition,
    });
  }

  handleVoiceUnsupportedTooltipClose(e) {
    if (this.state.voiceRecognitionSupported) return;

    // The pointer drifting off the 26px button must not close a tooltip the
    // keyboard opened — only a real blur ends a focus-driven one.
    if (e && e.type === 'blur') this.voiceUnsupportedTooltipFocused = false;
    else if (this.voiceUnsupportedTooltipFocused) return;

    this.clearVoiceUnsupportedTooltipHideTimer();
    this.voiceUnsupportedTooltipHideTimer = setTimeout(() => {
      this.voiceUnsupportedTooltipHideTimer = null;
      this.bindVoiceUnsupportedTooltipReflow(false);
      this.safeSetState({ voiceUnsupportedTooltipVisible: false });
    }, 120);
  }

  handleVoiceUnsupportedTooltipKeyDown(e) {
    if (e.key !== 'Escape' || !this.state.voiceUnsupportedTooltipVisible) return;

    e.preventDefault();
    e.stopPropagation();
    this.clearVoiceUnsupportedTooltipHideTimer();
    this.bindVoiceUnsupportedTooltipReflow(false);
    this.safeSetState({ voiceUnsupportedTooltipVisible: false });
  }

  // Closes the bubble for this hover only. It must reappear the next time the
  // control is hovered — "designed tooltip on hover" is an acceptance criterion,
  // and the native title stays suppressed, so a permanent dismissal would leave
  // no explanation at all for why voice is disabled.
  handleVoiceUnsupportedTooltipDismiss(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.voiceUnsupportedTooltipFocused = false;
    this.clearVoiceUnsupportedTooltipHideTimer();
    this.bindVoiceUnsupportedTooltipReflow(false);
    this.safeSetState({ voiceUnsupportedTooltipVisible: false });
  }

  handleVoiceToggle() {
    this.voice.toggleListening();
  }

  handleVoiceEnd() {
    this.voice.end();
    this.safeSetState((prev) => ({
      aiInputText: '',
      aiInputClearToken: prev.aiInputClearToken + 1,
      shouldSpeakNextAiResponse: false,
    }));
  }

  handleMicMuteToggle() {
    this.voice.toggleMicMute();
  }

  handleVoiceOutputMuteToggle() {
    this.voice.toggleOutputMute();
    if (this.voice.getState().voiceOutputMuted) {
      this.setState({ shouldSpeakNextAiResponse: false });
    }
  }

  handleAssistantResponseComplete(text) {
    if (!this.state.shouldSpeakNextAiResponse) return;

    this.setState({ shouldSpeakNextAiResponse: false });
    if (!this.state.voiceOutputSupported || this.state.voiceOutputMuted) return;
    this.voice.speak(text);
  }

  handleSearchModeChange(searchMode) {
    if (searchMode === this.state.searchMode) return;
    this.voice.reset();
    this.voice.setBusy(false);
    this.setState((prev) => ({
      searchMode,
      chatOpen: false,
      aiPendingPrompt: null,
      aiInputText: '',
      aiSubmitError: null,
      voiceError: null,
      voiceNetworkError: false,
      chatLoading: false,
      aiChatHasContent:
        searchMode === SearchModeOptions.AI ? hasPersistentSearchChatContent() : false,
      chatResetToken: prev.chatResetToken + 1,
      voiceTranscript: '',
      voiceModeActive: false,
      shouldSpeakNextAiResponse: false,
    }));
    this.props.clearSuggestion();

    const inputEl = this.searchContainerRef.current?.querySelector('input');
    if (inputEl && document.activeElement !== inputEl) {
      inputEl.placeholder =
        searchMode === SearchModeOptions.AI
          ? "Describe what you're looking for"
          : LOCATION_SEARCH_PLACEHOLDER;
    }
  }

  handleAiSearchSubmit(rawText, options = {}) {
    const candidateText = typeof rawText === 'string' ? rawText : this.state.aiInputText;
    const text = (candidateText || '').trim();
    if (!text) return;

    // The input is disabled while chatLoading (see SuggestedInput's
    // `disabled` prop below), so this only guards the voice-submit path,
    // which can still fire mid-response.
    if (this.state.chatLoading) return;

    this.voice.cancelSpeech();
    this.voice.clearTranscript();
    this.setState((prev) => ({
      // Force chatOpen on every submission, voice included: a voice-originated
      // turn that runs with the panel collapsed leaves VoiceTranscriptActivity
      // (display:none while chatOpen is false) permanently invisible, so the
      // recognized-text transcript never renders per AC.
      chatOpen: true,
      aiPendingPrompt: text,
      aiInputText: '',
      aiSubmitError: null,
      voiceError: null,
      aiChatHasContent: true,
      aiInputClearToken: prev.aiInputClearToken + 1,
      voiceTranscript: '',
      shouldSpeakNextAiResponse: Boolean(
        options.voice && prev.voiceOutputSupported && !prev.voiceOutputMuted
      ),
    }));
    this.props.clearSuggestion();
  }

  handleExpandToggle() {
    if (!this.state.aiChatHasContent && !this.state.chatOpen) {
      return;
    }
    this.setState((prev) => ({ chatOpen: !prev.chatOpen }));
  }

  handleChatContentChange(hasContent) {
    this.setState((prev) => {
      const chatOpen = hasContent ? prev.chatOpen : false;
      if (prev.aiChatHasContent === hasContent && prev.chatOpen === chatOpen) {
        return null;
      }
      return {
        aiChatHasContent: hasContent,
        chatOpen,
      };
    });
  }

  handleChatLoadingChange(isLoading) {
    this.voice.setBusy(isLoading);
    this.setState({ chatLoading: isLoading }, () => {
      if (isLoading) return;
      if (this.state.voiceModeActive) {
        this.voice.restartIfReady();
        return;
      }
      // ATAI-202: Voice mode resumes "Listening..." on its own via
      // restartIfReady above; text mode has no equivalent, so
      // the input was left blurred once the stream response landed, forcing
      // the user to click back in before typing a follow-up.
      this.focusAiSearchInputIfActive();
    });
  }

  // Refocuses the AI Search text input once a response finishes streaming,
  // matching Voice Search's auto-resume via this.voice.restartIfReady().
  // No-op outside AI text mode, or if the input already has focus.
  focusAiSearchInputIfActive() {
    if (this.state.searchMode !== SearchModeOptions.AI || this.state.voiceModeActive) return;
    const inputEl = this.searchContainerRef.current?.querySelector('input');
    if (inputEl && document.activeElement !== inputEl) {
      inputEl.focus({ preventScroll: true });
    }
  }

  // Clears a typed-but-not-yet-submitted AI prompt (chat isn't open yet, so
  // there's nothing to "close" — bump the remount token so SuggestedInput's
  // own internal typed value resets too, same trick as the mode switch).
  handleClearAiInput() {
    this.setState((prev) => ({ aiInputText: '', aiInputClearToken: prev.aiInputClearToken + 1 }));
    this.props.clearSuggestion();
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.clearVoiceUnsupportedTooltipHideTimer();
    this.bindVoiceUnsupportedTooltipReflow(false);
    this.voice.destroy();
    this.props.setFavoriteItem('');
    this.handleClearSuggestion();
  }

  handleClear() {
    this.voice.reset();
    this.voice.setBusy(false);
    // Rotate the persisted session id so the chatResetToken-driven remount
    // below actually starts a new conversation (PR #3361 review, MAJOR).
    resetPersistentSearchSession();
    this.setState((prev) => ({
      searchItem: '',
      chatOpen: false,
      aiPendingPrompt: null,
      aiInputText: '',
      aiSubmitError: null,
      voiceError: null,
      voiceNetworkError: false,
      chatLoading: false,
      aiChatHasContent: false,
      aiInputClearToken: prev.aiInputClearToken + 1,
      chatResetToken: prev.chatResetToken + 1,
      voiceTranscript: '',
      voiceModeActive: false,
      shouldSpeakNextAiResponse: false,
    }));
    this.props.setInputFlag(false);
    this.props.setFavoriteItem('');
    if (typeof this.props.onClearAll === 'function') {
      this.props.onClearAll();
    }
    this.props.clearAll();
    this.props.setSelectedSaveSearch(null);
    this.props.clearLeadListApiData();
    this.props.clearFilter();
    this.props.clearLocalFilter();
  }

  handleClearSuggestion() {
    this.props.clearSuggestion();
    // SuggestedInput tracks its own typed-but-not-selected value internally
    // (src/components/SuggestedInput: state.value takes precedence over the
    // `value` prop), so clearing searchItem alone doesn't clear what's on
    // screen — bump aiInputClearToken to remount it via its key, same trick
    // handleClear/handleClearAiInput use.
    this.safeSetState((prev) => ({
      searchItem: '',
      aiInputClearToken: prev.aiInputClearToken + 1,
    }));
    this.props.setInputFlag(false);
  }

  handleChangeSuggestion(item) {
    this.props.setInputFlag(false);
    const { setSuggestionValue, setRecentSuggestions } = this.props;
    this.setState({ searchItem: item });

    const inputEl = this.searchContainerRef.current?.querySelector('input');
    if (inputEl) inputEl.placeholder = '';

    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    setTimeout(() => {
      this.searchContainerRef.current?.focus({ preventScroll: true });
    }, 0);
    const loc = item;

    if (loc) {
      const { type, cityId, id } = loc;

      if (type === 'P') loc.propertyId = id;
      else if (!cityId && type === 'C') loc.cityId = id;
      else if (type === 'T') loc.stateId = id;

      setRecentSuggestions(setRecentSuggestion(loc));
    }
    this.props.onClearAll();
    setSuggestionValue(loc);
  }

  handleFetchSuggestion(query) {
    const { setSuggestionLabel, searchSuggestions, setSuggestions } = this.props;

    this.setState({ aiInputText: query });
    setSuggestionLabel(query);

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }

    if (query && query.length >= 4 && this.state.searchMode !== SearchModeOptions.AI) {
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;

        // const orangeResults = [{"id":21432,"fips":"059","cityName":"Orange","stateCode":"CA","countyId":256,"latitude":33.78779,"longitude":-117.85311,"coded":false,"type":"C","title":"Orange, CA","label":"Orange, CA"},{"id":31119,"fips":"019","cityName":"Orange Park","stateCode":"FL","countyId":367,"latitude":30.16607,"longitude":-81.70648,"coded":false,"type":"C","title":"Orange Park, FL","label":"Orange Park, FL"},{"id":167551,"fips":"361","cityName":"Orange","stateCode":"TX","countyId":2541,"latitude":30.09299,"longitude":-93.73655,"coded":false,"type":"C","title":"Orange, TX","label":"Orange, TX"},{"id":150989,"fips":"075","cityName":"Orangeburg","stateCode":"SC","countyId":2211,"latitude":33.49182,"longitude":-80.85565,"coded":false,"type":"C","title":"Orangeburg, SC","label":"Orangeburg, SC"},{"id":3377,"fips":"003","cityName":"Orange Beach","stateCode":"AL","countyId":74,"latitude":30.29437,"longitude":-87.57359,"coded":false,"type":"C","title":"Orange Beach, AL","label":"Orange Beach, AL"},{"id":256,"fips":"059","cityName":"Orange County","stateCode":"CA","countyId":256,"latitude":33.74279,"longitude":-117.90784,"coded":false,"type":"N","title":"Orange County, CA","label":"Orange County, CA"},{"id":405,"fips":"095","cityName":"Orange County","stateCode":"FL","countyId":405,"latitude":28.5563,"longitude":-81.4096,"coded":false,"type":"N","title":"Orange County, FL","label":"Orange County, FL"},{"id":1758,"fips":"071","cityName":"Orange County","stateCode":"NY","countyId":1758,"latitude":41.43065,"longitude":-74.25543,"coded":false,"type":"N","title":"Orange County, NY","label":"Orange County, NY"},{"id":2211,"fips":"075","cityName":"Orangeburg County","stateCode":"SC","countyId":2211,"latitude":33.49849,"longitude":-80.85565,"coded":false,"type":"N","title":"Orangeburg County, SC","label":"Orangeburg County, SC"},{"id":2541,"fips":"361","cityName":"Orange County","stateCode":"TX","countyId":2541,"latitude":30.1316,"longitude":-93.83878,"coded":false,"type":"N","title":"Orange County, TX","label":"Orange County, TX"},{"id":1851,"fips":"135","cityName":"Orange County","stateCode":"NC","countyId":1851,"latitude":36.00264,"longitude":-79.06918,"coded":false,"type":"N","title":"Orange County, NC","label":"Orange County, NC"},{"id":2715,"fips":"137","cityName":"Orange County","stateCode":"VA","countyId":2715,"latitude":38.24305,"longitude":-78.010415,"coded":false,"type":"N","title":"Orange County, VA","label":"Orange County, VA"},{"id":745,"fips":"117","cityName":"Orange County","stateCode":"IN","countyId":745,"latitude":38.55839,"longitude":-86.46832,"coded":false,"type":"N","title":"Orange County, IN","label":"Orange County, IN"},{"id":2647,"fips":"017","cityName":"Orange County","stateCode":"VT","countyId":2647,"latitude":43.98757,"longitude":-72.3226,"coded":false,"type":"N","title":"Orange County, VT","label":"Orange County, VT"},{"id":22110,"fips":"067","cityName":"Orangevale","stateCode":"CA","countyId":260,"latitude":38.67851,"longitude":-121.22578,"coded":false,"type":"C","title":"Orangevale, CA","label":"Orangevale, CA"},{"id":38813,"fips":"127","cityName":"Orange City","stateCode":"FL","countyId":421,"latitude":28.94888,"longitude":-81.29867,"coded":false,"type":"C","title":"Orange City, FL","label":"Orange City, FL"},{"id":180924,"fips":"137","cityName":"Orange","stateCode":"VA","countyId":2715,"latitude":38.24541,"longitude":-78.11083,"coded":false,"type":"C","title":"Orange, VA","label":"Orange, VA"},{"id":27939,"fips":"009","cityName":"Orange","stateCode":"CT","countyId":351,"latitude":41.27843,"longitude":-73.02566,"coded":false,"type":"C","title":"Orange, CT","label":"Orange, CT"},{"id":105522,"fips":"013","cityName":"Orange","stateCode":"NJ","countyId":1677,"latitude":40.77066,"longitude":-74.23265,"coded":false,"type":"C","title":"Orange, NJ","label":"Orange, NJ"},{"id":166079,"fips":"249","cityName":"Orange Grove","stateCode":"TX","countyId":2489,"latitude":27.95668,"longitude":-97.93694,"coded":false,"type":"C","title":"Orange Grove, TX","label":"Orange Grove, TX"},{"id":59587,"fips":"167","cityName":"Orange City","stateCode":"IA","countyId":837,"latitude":43.00721,"longitude":-96.05835,"coded":false,"type":"C","title":"Orange City, IA","label":"Orange City, IA"},{"id":83144,"fips":"011","cityName":"Orange","stateCode":"MA","countyId":1162,"latitude":42.59036,"longitude":-72.30981,"coded":false,"type":"C","title":"Orange, MA","label":"Orange, MA"},{"id":18806,"fips":"019","cityName":"Orange Cove","stateCode":"CA","countyId":237,"latitude":36.62439,"longitude":-119.31373,"coded":false,"type":"C","title":"Orange Cove, CA","label":"Orange Cove, CA"},{"id":114132,"fips":"087","cityName":"Orangeburg","stateCode":"NY","countyId":1766,"latitude":41.04649,"longitude":-73.94958,"coded":false,"type":"C","title":"Orangeburg, NY","label":"Orangeburg, NY"},{"id":139313,"fips":"037","cityName":"Orangeville","stateCode":"PA","countyId":2130,"latitude":41.07814,"longitude":-76.41439,"coded":false,"type":"C","title":"Orangeville, PA","label":"Orangeville, PA"},{"id":170764,"fips":"015","cityName":"Orangeville","stateCode":"UT","countyId":2621,"latitude":39.22719,"longitude":-111.0535,"coded":false,"type":"C","title":"Orangeville, UT","label":"Orangeville, UT"},{"id":52827,"fips":"177","cityName":"Orangeville","stateCode":"IL","countyId":673,"latitude":42.46835,"longitude":-89.64401,"coded":false,"type":"C","title":"Orangeville, IL","label":"Orangeville, IL"},{"id":104075,"fips":"009","cityName":"Orange","stateCode":"NH","countyId":1665,"latitude":43.65452,"longitude":-71.97147,"coded":false,"type":"C","title":"Orange, NH","label":"Orange, NH"},{"id":174565,"fips":"017","cityName":"Orange","stateCode":"VT","countyId":2647,"latitude":44.1484,"longitude":-72.4026,"coded":false,"type":"C","title":"Orange, VT","label":"Orange, VT"},{"id":126199,"fips":"035","cityName":"Orange","stateCode":"OH","countyId":1944,"latitude":41.44978,"longitude":-81.48067,"coded":false,"type":"C","title":"Orange, OH","label":"Orange, OH"},{"id":131273,"fips":"155","cityName":"Orangeville","stateCode":"OH","countyId":1996,"latitude":41.33922,"longitude":-80.51896,"coded":false,"type":"C","title":"Orangeville, OH","label":"Orangeville, OH"},{"id":167552,"fips":"361","cityName":"Orangefield","stateCode":"TX","countyId":2541,"latitude":30.07493,"longitude":-93.85573,"coded":false,"type":"C","title":"Orangefield, TX","label":"Orangefield, TX"},{"id":126015,"fips":"031","cityName":"Orange","stateCode":"OH","countyId":1943,"latitude":40.28813,"longitude":-81.6829,"coded":false,"type":"C","title":"Orange, OH","label":"Orange, OH"}];
        // this.props.setSuggestions(orangeResults);

        const results = parseSuggestion(query);

        if (results.length) setSuggestions(results);
        else searchSuggestions(query);
      }, 800);
    }

    return Promise.resolve([]);
  }

  handleFavoriteClick(search) {
    const tab = search.toJS();
    this.props.setFavoriteItem(tab);
    this.props.onClearAll();
    const favoriteCounts = this.props.favoriteCounts || new Map();
    const { location } = this.props;

    const hasExplicitLocation = tab.cityId || tab.countyId || tab.zip || tab.shapeDefinition;
    if (!hasExplicitLocation && location && location.shapeDefinition) {
      tab.shapeDefinition = location.shapeDefinition;
    }

    const count = favoriteCounts.get(String(tab.id), 0);
    tab.totalCount = count;
    if (this.props.suggestionValue || tab.totalCount != null) {
      this.props.loadSearch(tab);
      this.props.setInputFlag(false);
    }
  }

  render() {
    const {
      location,
      favoriteSearches,
      favoriteCounts,
      favoriteLoading,
      favoriteStale,
      recentLoaded,
      loading,
      label,
      items,
      query,
      suggestionLoading,
      favoriteSelectedName,
      currentFilters,
      localFilter,
      pageVisible,
    } = this.props;
    const {
      searchMode,
      aiInputText,
      aiInputClearToken,
      chatOpen,
      aiPendingPrompt,
      chatLoading,
      aiChatHasContent,
      chatResetToken,
      voiceError,
      voiceRecognitionSupported,
      voiceOutputSupported,
      voiceListening,
      voiceModeActive,
      voiceSpeaking,
      voiceTranscript,
      micMuted,
      voiceOutputMuted,
      voiceReplyPlaying,
      voiceAudioReactive,
      voiceWaveformSamples,
      micPermissionBlocked,
      voiceNetworkError,
      voiceUnsupportedTooltipVisible,
      voiceUnsupportedTooltipPosition,
    } = this.state;
    const isAiSearchMode = searchMode === SearchModeOptions.AI;
    const isVoiceControlsActive =
      isAiSearchMode && (voiceModeActive || voiceListening || voiceSpeaking || voiceReplyPlaying);
    const isMicControlActive = isAiSearchMode && voiceListening && !micMuted;
    // Muting the mic stops recognition but deliberately keeps voice mode alive
    // (handleMicMuteToggle), so voiceModeActive on its own kept the status line
    // claiming 'Listening...' while the mic was off. 'AI is responding...'
    // still outranks it — spoken output is playing either way.
    const voiceInputStatus =
      voiceReplyPlaying || (chatLoading && voiceModeActive)
        ? 'AI is responding...'
        : micMuted
          ? 'Microphone muted'
          : voiceListening || voiceModeActive
            ? 'Listening...'
            : '';
    const voiceButtonDisabled =
      !voiceRecognitionSupported || micMuted || chatLoading || voiceReplyPlaying;
    const expandDisabled = !aiChatHasContent && !chatOpen;
    const shouldShowVoiceWaveform = voiceSpeaking;
    const voiceButtonTitle = !voiceRecognitionSupported
      ? VOICE_UNSUPPORTED_BROWSER_TOOLTIP
      : chatLoading || voiceReplyPlaying
        ? 'Please wait for the current response to finish'
        : micMuted
          ? 'Microphone is muted'
          : voiceListening
            ? 'Stop listening'
            : 'Start voice search';
    const micMuteTitle = !voiceRecognitionSupported
      ? VOICE_UNSUPPORTED_BROWSER_TOOLTIP
      : micMuted
        ? 'Unmute microphone'
        : 'Mute microphone';
    const voiceOutputTitle = !voiceOutputSupported
      ? 'AI voice output is not supported in this browser'
      : voiceOutputMuted
        ? 'Unmute AI voice output'
        : 'Mute AI voice output';
    const showVoiceUnsupportedTooltip = !voiceRecognitionSupported;
    const voiceUnsupportedTooltipOpen =
      showVoiceUnsupportedTooltip && voiceUnsupportedTooltipVisible && voiceUnsupportedTooltipPosition;

    return (
      <header className={css.appHeader}>
        <div className={css.top}>
          <div className={css.row}>
            <div className={css.col}>
              <div className={css.searchInput} ref={this.searchContainerRef} tabIndex="-1">
                <div
                  className={classNames(css.search, {
                    [css.searchChatOpen]: isAiSearchMode && chatOpen,
                    [css.searchVoiceActive]: isVoiceControlsActive,
                    [css.searchAiFocus]: isAiSearchMode && !voiceListening,
                    [css.searchVoiceListening]: voiceListening,
                  })}
                >
                  <SearchModeToggle
                    className={css.searchModeToggleInline}
                    mode={searchMode}
                    onChange={this.handleSearchModeChange}
                  />
                  <div className={css.searchModeDivider} ref={this.searchModeDividerRef} />
                  <div
                    className={classNames(css.suggestedInputWrapper, {
                      [css.voiceStatusVisible]: isVoiceControlsActive && Boolean(voiceInputStatus),
                    })}
                  >
                    <SuggestedInput
                      // Remount on mode switch so its internally-typed value
                      // (not controlled by Header) actually clears visually.
                      key={`${searchMode}-${aiInputClearToken}`}
                      // searchValue={searchValue}
                      isAiSearchMode={isAiSearchMode}
                      isRecent={recentLoaded}
                      value={label}
                      suggestions={items}
                      loading={suggestionLoading}
                      onChange={this.handleChangeSuggestion}
                      onFetch={this.handleFetchSuggestion}
                      // onFocus={loadRecentSuggestions}
                      clearSearch={this.handleClearSuggestion}
                      placeholder={this.getPlaceholder()}
                      // ATAI-215: Voice Mode is speech-only — no keystrokes.
                      disabled={isAiSearchMode && (chatLoading || isVoiceControlsActive)}
                      onFocus={(e) => (e.target.placeholder = '')}
                      // onBlur={(e) => e.target.placeholder = this.getPlaceholder()}
                    />
                    {isVoiceControlsActive && voiceInputStatus && (
                      <div className={css.voiceInputStatus} aria-hidden="true">
                        {shouldShowVoiceWaveform ? (
                          renderVoiceWaveform(voiceWaveformSamples, voiceAudioReactive)
                        ) : (
                          <span>{voiceInputStatus}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={css.actionIcons}>
                    {/* ATAI-190: in AI mode this only ever clears the typed
                        input — same as Location Search's own clear — never
                        closes/resets the chat session, so an open
                        conversation and its history survive clearing the
                        box. Hidden once there's nothing typed to clear.
                        ATAI-215: also hidden in Voice Mode — the live
                        transcript writes aiInputText, which isn't typed input. */}
                    {(!isAiSearchMode || (aiInputText?.trim() && !isVoiceControlsActive)) && (
                      <Button
                        kind="link-default"
                        name="clear"
                        className={css.btnClear}
                        onClick={
                          !isAiSearchMode ? this.handleClearSuggestion : this.handleClearAiInput
                        }
                      >
                        <img src={clearSearch} alt="Close" className={css.iconClear} />
                      </Button>
                    )}

                    {isAiSearchMode && !isVoiceControlsActive && (
                      <>
                        <Button
                          kind="link-default"
                          name="expand"
                          className={classNames(css.aiSearchicons, css.aiExpandToggle, {
                            [css.aiExpandBtn]: !chatOpen,
                            [css.aiCollapseBtn]: chatOpen,
                          })}
                          disabled={expandDisabled}
                          aria-label={
                            chatOpen ? 'Collapse AI Search chat' : 'Expand AI Search chat'
                          }
                          onClick={this.handleExpandToggle}
                        >
                          {renderExpandCollapseIcon(chatOpen)}
                        </Button>

                        <span
                          ref={this.voiceTooltipHostRef}
                          className={css.aiVoiceControlWrapper}
                          title={voiceRecognitionSupported ? voiceButtonTitle : undefined}
                          aria-label={voiceButtonTitle}
                          aria-describedby={
                            voiceUnsupportedTooltipOpen
                              ? VOICE_UNSUPPORTED_BROWSER_TOOLTIP_ID
                              : undefined
                          }
                          aria-disabled={voiceButtonDisabled}
                          tabIndex={voiceButtonDisabled ? 0 : undefined}
                          // These no-op unless voice is unsupported — each handler guards itself.
                          onMouseEnter={this.handleVoiceUnsupportedTooltipOpen}
                          onMouseLeave={this.handleVoiceUnsupportedTooltipClose}
                          onFocus={this.handleVoiceUnsupportedTooltipOpen}
                          onBlur={this.handleVoiceUnsupportedTooltipClose}
                          onKeyDown={this.handleVoiceUnsupportedTooltipKeyDown}
                        >
                          <Button
                            kind="link-default"
                            name="ai-voice"
                            className={classNames(css.aiSearchicons, css.aiVoiceButton, {
                              [css.voiceUnsupported]: !voiceRecognitionSupported,
                            })}
                            disabled={voiceRecognitionSupported && voiceButtonDisabled}
                            aria-label={voiceButtonTitle}
                            aria-pressed={voiceListening}
                            onClick={this.handleVoiceToggle}
                          >
                            <img src={aiVoiceIcon} alt="" aria-hidden="true" />
                          </Button>
                        </span>
                      </>
                    )}

                    {isVoiceControlsActive && (
                      <>
                        <span
                          className={css.aiVoiceControlWrapper}
                          title={micMuteTitle}
                          aria-label={micMuteTitle}
                          aria-disabled={!voiceRecognitionSupported}
                          tabIndex={!voiceRecognitionSupported ? 0 : undefined}
                        >
                          <Button
                            kind="link-default"
                            name="ai-mic-mute"
                            className={classNames(css.aiVoiceControl, {
                              [css.voiceMicActive]: isMicControlActive,
                              [css.voiceMicMuted]: micMuted,
                            })}
                            disabled={!voiceRecognitionSupported}
                            aria-label={micMuteTitle}
                            aria-pressed={micMuted}
                            onClick={this.handleMicMuteToggle}
                          >
                            {renderMicControlIcon(micMuted)}
                          </Button>
                        </span>

                        <span
                          className={css.aiVoiceControlWrapper}
                          title={voiceOutputTitle}
                          aria-label={voiceOutputTitle}
                          aria-disabled={!voiceOutputSupported}
                          tabIndex={!voiceOutputSupported ? 0 : undefined}
                        >
                          <Button
                            kind="link-default"
                            name="ai-voice-mute"
                            className={classNames(css.aiVoiceControl, {
                              [css.voiceOutputMuted]: voiceOutputMuted,
                            })}
                            disabled={!voiceOutputSupported}
                            aria-label={voiceOutputTitle}
                            aria-pressed={voiceOutputMuted}
                            onClick={this.handleVoiceOutputMuteToggle}
                          >
                            {renderSoundControlIcon(voiceOutputMuted)}
                          </Button>
                        </span>

                        <Button
                          kind="link-default"
                          name="expand"
                          className={classNames(css.aiSearchicons, css.aiExpandToggle, {
                            [css.aiExpandBtn]: !chatOpen,
                            [css.aiCollapseBtn]: chatOpen,
                          })}
                          disabled={expandDisabled}
                          aria-label={
                            chatOpen ? 'Collapse AI Search chat' : 'Expand AI Search chat'
                          }
                          onClick={this.handleExpandToggle}
                        >
                          {renderExpandCollapseIcon(chatOpen)}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {/*
                  ATAI-242: portalled to <body> rather than nested here. .search
                  clips (overflow:hidden for its pill radius) and .appHeader is a
                  z-index:2 stacking context, so a bubble inside the header can
                  never paint above .chatPanel — that panel is itself a body-level
                  portal at z-index:3. Position is therefore computed from the
                  control's rect and refreshed on scroll/resize.
                */}
                {voiceUnsupportedTooltipOpen && (
                  <Portal isOpened>
                    <span
                      id={VOICE_UNSUPPORTED_BROWSER_TOOLTIP_ID}
                      className={css.voiceUnsupportedTooltip}
                      role="note"
                      style={voiceUnsupportedTooltipPosition}
                      onMouseEnter={this.handleVoiceUnsupportedTooltipOpen}
                      onMouseLeave={this.handleVoiceUnsupportedTooltipClose}
                    >
                      <span>{VOICE_UNSUPPORTED_BROWSER_TOOLTIP}</span>
                      <button
                        type="button"
                        className={css.voiceUnsupportedTooltipClose}
                        aria-label="Dismiss voice browser tooltip"
                        onClick={this.handleVoiceUnsupportedTooltipDismiss}
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </span>
                  </Portal>
                )}
                <div
                  className={classNames(css.iconSearch, {
                    [css.iconSearchEnd]: isVoiceControlsActive,
                    [css.iconSearchAi]: isAiSearchMode && !isVoiceControlsActive,
                    [css.disabled]:
                      !isVoiceControlsActive &&
                      (isAiSearchMode
                        ? !aiInputText?.trim() || chatLoading
                        : !(items?.length && label?.length >= 4 && !loading)),
                  })}
                  onClick={
                    isVoiceControlsActive
                      ? this.handleVoiceEnd
                      : isAiSearchMode
                        ? this.handleAiSearchSubmit
                        : () => this.handleChangeSuggestion(items[0])
                  }
                >
                  <span
                    className={css.searchText}
                    style={
                      isAiSearchMode && !isVoiceControlsActive
                        ? { color: 'var(--Periwinkle-500, #5B68C3)' }
                        : undefined
                    }
                  >
                    {isVoiceControlsActive && renderEndCallIcon()}
                    {isVoiceControlsActive ? 'End' : isAiSearchMode ? 'AI Search' : 'Search'}
                  </span>
                </div>
                {isAiSearchMode && voiceError && (
                  <div className={css.chatLimitError} role="alert" aria-live="assertive">
                    {voiceError}
                  </div>
                )}
                <SearchChatPanel
                  // Remounts whenever a close action (mode switch, Clear All,
                  // or the close icon) ends the conversation, so the next
                  // open starts with a fresh sessionId/history/messages
                  // instead of replaying the prior conversation.
                  key={chatResetToken}
                  // Portal content escapes this page's own display:none-based
                  // visibility (the app keeps pages mounted and just toggles
                  // display via the `visible` prop, rather than unmounting on
                  // navigation) — fold pageVisible in so the panel actually
                  // hides when the user navigates to another page.
                  open={chatOpen && pageVisible !== false}
                  initialPrompt={aiPendingPrompt}
                  onConsumeInitialPrompt={() => this.setState({ aiPendingPrompt: null })}
                  onLoadingChange={this.handleChatLoadingChange}
                  onSubmitError={(message) => this.setState({ aiSubmitError: message })}
                  onAssistantResponseComplete={this.handleAssistantResponseComplete}
                  onChatContentChange={this.handleChatContentChange}
                  voiceTranscript={voiceTranscript}
                  voiceActive={isVoiceControlsActive}
                  // S8's "AI Search is temporarily unavailable" modal offers
                  // Location Search as the fallback — same switch the mode
                  // toggle performs, so it closes the chat and resets state.
                  onUseLocationSearch={() =>
                    this.handleSearchModeChange(SearchModeOptions.LOCATION)
                  }
                  anchorRef={this.searchContainerRef}
                  dividerRef={this.searchModeDividerRef}
                  context={{
                    currentFilters: currentFilters?.toJS ? currentFilters.toJS() : currentFilters,
                    // The panel's own chip list, so the agent reports the same
                    // filters the user is looking at (ATAI-204).
                    appliedFilters: toAppliedFilterContext(localFilter),
                    currentLocation: location,
                    favoriteSearchName:
                      favoriteSelectedName?.name || favoriteSelectedName?.label || null,
                  }}
                />
              </div>
            </div>
            <div className={css.col}>
              <TagFilterProvider>
                <HeaderMlsFilter onSelect={this.props.onSelect} />
              </TagFilterProvider>
            </div>
            <div className={css.col}>
              <TagFilterProvider>
                <SearchFilterNew
                  setInputFlag={this.props.setInputFlag}
                  isAiSearchMode={isAiSearchMode}
                  pageVisible={pageVisible}
                />
              </TagFilterProvider>
            </div>
            <div className={css.col}>
              <NewUIFilterSaved />
            </div>
            <div className={css.col}>
              <Button
                kind="link-default"
                className={css.clearAllBtn}
                size="large"
                onClick={this.handleClear}
              >
                Clear All
              </Button>
            </div>
          </div>
        </div>
        <div className={css.bottom}>
          <div className={css.clsCardSection}>
            <div className={css.row}>
              {FavoriteSlots.map((idx) => {
                const search = favoriteSearches.get(String(idx), Map());
                const id = search.get('id');

                return (
                  <div key={idx} className={css.col}>
                    {!(favoriteLoading || favoriteStale) ? null : (
                      <HeaderSearchItem label="Loading" />
                    )}
                    {favoriteLoading || favoriteStale || !search.get('name') ? null : (
                      <RestrictedContent permission={!search.get('id') || search.get('enabled')}>
                        <HeaderSearchItem
                          newFeature={false}
                          label={search.get('name')}
                          value={!id ? null : favoriteCounts.get(String(id), 0)}
                          onClick={!id ? null : () => this.handleFavoriteClick(search)}
                          isActive={
                            (favoriteSelectedName &&
                              favoriteSelectedName?.value === search.get('listingType') &&
                              favoriteSelectedName?.label === search.get('name')) ||
                            (favoriteSelectedName?.listingType === search.get('listingType') &&
                              favoriteSelectedName?.name === search.get('name'))
                          }
                        />
                      </RestrictedContent>
                    )}
                  </div>
                );
              })}
              <div className={css.col}>
                <RestrictedContent permission={Permissions.searchFull}>
                  <AllSearches query={query} />
                </RestrictedContent>
              </div>
            </div>
          </div>
        </div>
        {micPermissionBlocked && (
          <MicPermissionModal
            onDismiss={this.handleMicPermissionDismiss}
            onRetry={this.handleMicPermissionRetry}
          />
        )}
        {!micPermissionBlocked && voiceNetworkError && (
          <MicPermissionModal
            title={VOICE_NETWORK_ERROR_TITLE}
            body={null}
            onDismiss={this.handleVoiceNetworkDismiss}
            onRetry={this.handleVoiceNetworkRetry}
          />
        )}
      </header>
    );
  }
}

export default connect(
  (state) => {
    const suggestion = selectSuggestion(state).toJS();
    return {
      ...suggestion,
      suggestionLoading: suggestion.loading,
      suggestionValue: suggestion.value,
      favoriteSearches: selectFavoriteSearches(state),
      favoriteCounts: selectFavoriteSearchCounts(state),
      favoriteLoading: selectFavoriteSearchLoading(state),
      favoriteStale: selectFavoriteSearchStale(state),
      favoriteSelectedName: selectFavoriteSelectedName(state),
      currentFilters: selectFilter(state),
      // The Search Filters panel's applied-chip list — what its count badge
      // counts (searchFilterNew/SearchFilterNew.js), fed to AI Search as the
      // authoritative applied-filter state (ATAI-204).
      localFilter: selectLocalFilter(state),
      // `state.data.search.location` is not a path this store has — the search
      // slice sits at `state.search` — so the old guard fell through to `{}` on
      // every render and this prop never carried a location. Read it through
      // the slice's own selector, same as `currentFilters` above.
      location: selectLocation(state)?.toJS?.() || {},
    };
  },
  {
    clearSuggestion,
    loadSearch,
    setSuggestionValue,
    setSuggestionLabel,
    searchSuggestions,
    setRecentSuggestions,
    setSuggestions,
    clearFilter,
    clearLocalFilter,
    clearAll,
    clearLeadListApiData,
    setSelectedSaveSearch,
    setFavoriteItem,
    refreshSearch,
  }
)(Header);
